import {
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  message,
} from "antd";
import axios from "axios";
import React, { useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { serverUrl } from "../../../../nestserver";

const authHeader = () => ({
  Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
});

const STATUS_TAGS = {
  pending: { color: "gold", label: "Pending" },
  approved: { color: "green", label: "Approved" },
  partially_approved: { color: "blue", label: "Partly approved" },
  rejected: { color: "red", label: "Rejected" },
};

const errorText = (err) =>
  err?.response?.data?.error?.errors?.[0] || "Something went wrong";

/**
 * Instructor view of stock: what the school holds (read-only) and the requests
 * this instructor has raised against it.
 */
const InstructorStock = () => {
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);

  const available = useQuery("/school-stock/items/available", async () => {
    const res = await axios.get(`${serverUrl}/school-stock/items/available`, {
      headers: authHeader(),
    });
    return res.data;
  });

  const mine = useQuery("/school-stock/requests/mine", async () => {
    const res = await axios.get(`${serverUrl}/school-stock/requests`, {
      headers: authHeader(),
    });
    return res.data;
  });

  const items = available?.data?.result || [];
  const itemById = Object.fromEntries(items.map((item) => [String(item._id), item]));

  const closeNew = () => {
    form.resetFields();
    setShowNew(false);
  };

  const handleSubmit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (err) {
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${serverUrl}/school-stock/requests`,
        {
          items: values.items.map((line) => ({
            stock_item: line.stock_item,
            quantity_requested: line.quantity_requested,
          })),
          reason: values.reason || undefined,
        },
        { headers: authHeader() },
      );
      message.success("Request sent to the admin");
      closeNew();
      queryClient.invalidateQueries("/school-stock/requests/mine");
    } catch (err) {
      message.error(errorText(err));
    } finally {
      setSaving(false);
    }
  };

  const availableColumns = [
    { title: "Item", dataIndex: "name" },
    { title: "Category", dataIndex: "category", render: (v) => v || "—" },
    {
      title: "In hand",
      align: "center",
      render: (r) => (
        <span className="font-semibold">
          {r.quantity_in_hand} {r.unit}
        </span>
      ),
    },
    {
      title: "Availability",
      align: "center",
      render: (r) =>
        r.quantity_in_hand <= 0 ? (
          <Tag color="red">Out of stock</Tag>
        ) : (
          <Tag color="green">Available</Tag>
        ),
    },
  ];

  const requestColumns = [
    {
      title: "Request",
      width: 130,
      render: (r) => <span className="font-semibold">{r.request_id}</span>,
    },
    {
      title: "Items",
      render: (r) => (
        <div className="space-y-1">
          {r.items.map((line) => (
            <div key={line._id} className="text-sm">
              {line.item_name} — requested {line.quantity_requested} {line.unit}
              {r.status !== "pending" && (
                <span className="text-gray-500">
                  {" "}
                  (approved {line.quantity_approved})
                </span>
              )}
            </div>
          ))}
          {r.rejection_reason && (
            <div className="text-xs text-red-600">
              Rejected: {r.rejection_reason}
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Status",
      width: 150,
      align: "center",
      render: (r) => {
        const tag = STATUS_TAGS[r.status] || { color: "default", label: r.status };
        return <Tag color={tag.color}>{tag.label}</Tag>;
      },
    },
  ];

  return (
    <div className="p-8">
      <div className="bg-white p-10 rounded-2xl">
        <div className="flex items-center justify-between pb-8">
          <div className="text-2xl text-orange-500 font-semibold">
            Stock Inventory
          </div>
          <Button type="primary" onClick={() => setShowNew(true)}>
            New request
          </Button>
        </div>

        <div className="pb-2 text-lg font-semibold">Available in school</div>
        <Table
          rowKey="_id"
          className="rounded-2xl pb-8"
          loading={available.isLoading}
          columns={availableColumns}
          dataSource={items}
          pagination={{ pageSize: 10 }}
        />

        <div className="pb-2 text-lg font-semibold">My requests</div>
        <Table
          rowKey="_id"
          className="rounded-2xl"
          loading={mine.isLoading}
          columns={requestColumns}
          dataSource={mine?.data?.result || []}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 700 }}
        />
      </div>

      <Modal
        title="New stock request"
        open={showNew}
        onCancel={closeNew}
        onOk={handleSubmit}
        okText="Send request"
        confirmLoading={saving}
        destroyOnClose
        width={620}
      >
        <Form form={form} layout="vertical" className="pt-4">
          <Form.List
            name="items"
            initialValue={[{}]}
            rules={[
              {
                validator: async (_, value) => {
                  if (!value || value.length < 1) {
                    throw new Error("Add at least one item");
                  }
                },
              },
            ]}
          >
            {(fields, { add, remove }, { errors }) => (
              <>
                {fields.map((field) => {
                  const selectedId = form.getFieldValue(["items", field.name, "stock_item"]);
                  const maxQty = itemById[selectedId]?.quantity_in_hand;
                  return (
                    <Space key={field.key} align="start" className="w-full pb-2">
                      <Form.Item
                        {...field}
                        name={[field.name, "stock_item"]}
                        rules={[{ required: true, message: "Pick an item" }]}
                        style={{ width: 300, marginBottom: 0 }}
                      >
                        <Select
                          showSearch
                          optionFilterProp="label"
                          placeholder="Select item"
                          options={items.map((item) => ({
                            value: String(item._id),
                            label: `${item.name} (${item.quantity_in_hand} ${item.unit})`,
                            disabled: item.quantity_in_hand <= 0,
                          }))}
                        />
                      </Form.Item>
                      <Form.Item
                        {...field}
                        name={[field.name, "quantity_requested"]}
                        rules={[
                          { required: true, message: "Enter a quantity" },
                          {
                            validator: async (_, value) => {
                              if (maxQty !== undefined && value > maxQty) {
                                throw new Error(`Only ${maxQty} in hand`);
                              }
                            },
                          },
                        ]}
                        style={{ marginBottom: 0 }}
                      >
                        <InputNumber min={1} max={maxQty} precision={0} placeholder="Qty" />
                      </Form.Item>
                      <Button danger onClick={() => remove(field.name)}>
                        Remove
                      </Button>
                    </Space>
                  );
                })}
                <Form.ErrorList errors={errors} />
                <Button type="dashed" block onClick={() => add()}>
                  Add item
                </Button>
              </>
            )}
          </Form.List>

          <Form.Item name="reason" label="Reason (optional)" className="pt-4">
            <Input.TextArea rows={2} placeholder="e.g. For the Grade 3 science practical" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default InstructorStock;
